import { useMemo, useState } from 'react';
import { formatMoney, formatTime, panelStage, t } from '@aquabox/shared';
import { plateAndPerson, regionText } from '../lib/vehicle-label';
import { usePanelFleet, type PanelFleetGroup, type PanelFleetSource } from '../api/panel-fleet';

/**
 * Seferler ekranındaki "Panel araçları — bugün" kartı.
 *
 * Bayi paneli (Hayat/Abant) siparişleri panelin ham durumuna göre sayım ve açık liste olarak
 * özetlenir. Şoförlerin mobil uygulaması olmayan, panelden yönetilen araçlar burada görünür;
 * bu kart `Trip` tablosunun İKİNCİL bir katmanıdır. `Trips.tsx` ana kartı boşken de bu kart
 * dolu olabilir — o zaman kısa "trip empty" hâlâ altta durur.
 *
 * Defter DEĞİŞMEZ; salt okunur. Sorgu `usePanelFleet` 60 sn'de bir tazelenir.
 */
export function PanelFleetCard() {
  const data = usePanelFleet();
  const groups = data.data?.groups ?? [];
  const totalOpen = useMemo(() => groups.reduce((sum, g) => sum + g.open.length, 0), [groups]);

  // Yükleniyorsa ya da grup yoksa kart GÖRÜNMEZ. "Bugün panel araç siparişi yok" yerine geçen
  // boş durum kartın HİÇ olmamasıdır — Seferler ekranının zaten kısa bir boş durumu var.
  if (data.isPending || groups.length === 0) return null;

  return (
    <section
      className="card"
      data-testid="panel-fleet-card"
      style={{
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <strong style={{ fontSize: 15 }}>{t('panelFleet.title.today')}</strong>
        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {t('panelFleet.openCount', { n: totalOpen })}
        </span>
      </header>

      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {groups.map((g) => (
          <PanelFleetRow key={`${g.source}::${g.label ?? ''}`} group={g} />
        ))}
      </ul>
    </section>
  );
}

/** Tek satır: marka rozeti + ad + sayım satırı + son hareket + açılır liste. */
function PanelFleetRow({ group }: { group: PanelFleetGroup }) {
  const [open, setOpen] = useState(false);
  const stage = panelStage(group.source, 'Araç Listesinde');
  const brand = stage?.brand ?? (group.source === 'HAYAT_PANEL' ? 'Hayat' : 'Abant');
  const labelText = labelFor(group);

  return (
    <li
      data-testid={`panel-fleet-row-${group.source}-${group.label ?? 'null'}`}
      style={{
        borderRadius: 'var(--r-row)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          gap: 10,
          alignItems: 'center',
          padding: '10px 14px',
          background: 'transparent',
          border: 'none',
          textAlign: 'left',
          font: 'inherit',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 'var(--r-pill)',
            background: 'var(--primary-bg)',
            color: 'var(--primary)',
            boxShadow: 'inset 0 0 0 1px currentColor',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {brand}
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{labelText}</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {countsLineText(group.counts)}
            {group.deliveredAmount !== null
              ? ` · ${t('panelFleet.revenueLine', { amount: formatMoney(group.deliveredAmount) })}`
              : ''}
          </span>
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'right' }}>
          {group.lastActivityAt
            ? t('panelFleet.lastActivity', { time: formatTime(group.lastActivityAt) })
            : t('panelFleet.lastActivity.none')}
        </span>
      </button>

      {open && group.open.length > 0 ? (
        <ul
          data-testid={`panel-fleet-open-${group.source}-${group.label ?? 'null'}`}
          style={{
            listStyle: 'none',
            margin: 0,
            padding: '0 14px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            borderTop: '1px solid var(--border)',
          }}
        >
          {group.open.map((o) => (
            <li
              key={o.id}
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                gap: 10,
                alignItems: 'center',
                paddingTop: 8,
                fontSize: 12.5,
              }}
            >
              <span style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                {formatTime(o.receivedAt)}
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{o.customerName}</span>
                <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{o.address}</span>
              </span>
              <span
                style={{
                  padding: '1px 6px',
                  borderRadius: 'var(--r-pill)',
                  background: 'var(--bg-warning-soft)',
                  color: 'var(--warning)',
                  boxShadow: 'inset 0 0 0 1px currentColor',
                  fontSize: 11,
                  fontWeight: 600,
                  justifySelf: 'end',
                }}
              >
                {o.stageText}
              </span>
              <span
                style={{
                  gridColumn: '1 / -1',
                  textAlign: 'right',
                  fontSize: 12,
                  fontVariantNumeric: 'tabular-nums',
                  color: 'var(--text-secondary)',
                }}
              >
                {formatMoney(o.totalAmount)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * Sayım satırı: sıfır sayaçlar yazılmaz ("0 yolda" gösterilmez), yalnız sıfırdan büyük olanlar
 * listelenir. Hepsi sıfırsa "Bugün iş yok" (30.09.2026 kullanıcı kararı: kart gürültü yapmasın).
 */
function countsLineText(counts: PanelFleetGroup['counts']): string {
  const parts: string[] = [];
  if (counts.progress > 0) parts.push(t('panelFleet.countsProgress', { n: counts.progress }));
  if (counts.done > 0) parts.push(t('panelFleet.countsDone', { n: counts.done }));
  if (counts.new > 0) parts.push(t('panelFleet.countsNew', { n: counts.new }));
  if (counts.cancelled > 0) parts.push(t('panelFleet.countsCancelled', { n: counts.cancelled }));
  if (parts.length === 0) return t('panelFleet.countsAllZero');
  return parts.join(' · ');
}

/**
 * Görünen ad (Seferler + Araçlar & Ekip): "Plaka · Personel adı". Eşleme yoksa çıplak çalışan numarası
 * yerine okunur metin ("Bölge: Alt" / "Personel eşlenmemiş"); etiketsiz grup "Araç atanmamış".
 */
export function labelFor(group: PanelFleetGroup): string {
  const mapped = plateAndPerson(group.vehicle?.plate ?? null, group.displayName);
  if (mapped) return mapped;
  if (group.label === null) return t('panelFleet.unassigned');
  const region = regionText(group.label);
  if (region) return region;
  // Hayat etiketi ad/plaka olabilir (okunur); salt rakam çalışan numarasıdır, gösterilmez.
  if (group.source === 'HAYAT_PANEL' && !/^\d+$/.test(group.label.trim())) return group.label;
  return t('orders.vehicleLabel.unmapped');
}

export type { PanelFleetGroup, PanelFleetSource };
